import React from "react";
import { Link } from "react-router-dom";
import {
  Facebook,
  Twitter,
  Instagram,
  Youtube,
  Music,
  Mail,
  ArrowRight,
} from "lucide-react";
import Logo from "./Logo";
import { toast } from "./Toast";

const Footer: React.FC = () => {
  return (
    <footer className="relative bg-gradient-to-b from-background-dark via-background-dark to-background-card">
      {/* Gradient top border */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>

      {/* Subtle radial gradient overlays */}
      <div className="absolute inset-0 bg-gradient-radial-at-tr from-primary/5 via-transparent to-transparent opacity-70"></div>
      <div className="absolute inset-0 bg-gradient-radial-at-bl from-secondary/5 via-transparent to-transparent opacity-70"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8">
          <div className="col-span-1 md:col-span-4">
            <Logo className="h-10 w-auto" />
            <p className="mt-5 text-white-subtle text-base leading-relaxed">
              Your ultimate destination for music news, artist discovery,
              collaboration, and community.
            </p>
            <div className="mt-8 flex space-x-5">
              <a
                href="#"
                className="text-white-subtle hover:text-primary hover:text-glow-primary-subtle transition-all duration-250 p-2 rounded-full hover:bg-primary/5"
                aria-label="Facebook"
                rel="noopener noreferrer"
              >
                <Facebook size={20} />
              </a>
              <a
                href="#"
                className="text-white-subtle hover:text-primary hover:text-glow-primary-subtle transition-all duration-250 p-2 rounded-full hover:bg-primary/5"
                aria-label="Twitter"
                rel="noopener noreferrer"
              >
                <Twitter size={20} />
              </a>
              <a
                href="#"
                className="text-white-subtle hover:text-primary hover:text-glow-primary-subtle transition-all duration-250 p-2 rounded-full hover:bg-primary/5"
                aria-label="Instagram"
                rel="noopener noreferrer"
              >
                <Instagram size={20} />
              </a>
              <a
                href="#"
                className="text-white-subtle hover:text-primary hover:text-glow-primary-subtle transition-all duration-250 p-2 rounded-full hover:bg-primary/5"
                aria-label="Youtube"
                rel="noopener noreferrer"
              >
                <Youtube size={20} />
              </a>
              <a
                href="#"
                className="text-white-subtle hover:text-primary hover:text-glow-primary-subtle transition-all duration-250 p-2 rounded-full hover:bg-primary/5"
                aria-label="Music"
                rel="noopener noreferrer"
              >
                <Music size={20} />
              </a>
            </div>
          </div>

          <div className="md:col-span-2">
            <h3 className="text-sm font-semibold text-primary uppercase tracking-wider">
              Navigation
            </h3>
            <ul className="mt-5 space-y-3">
              <li>
                <Link
                  to="/"
                  className="text-white-subtle hover:text-white transition-colors duration-250 inline-flex items-center group"
                >
                  <span className="group-hover:translate-x-1 transition-transform duration-250">
                    Home
                  </span>
                </Link>
              </li>
              <li>
                <Link
                  to="/news"
                  className="text-white-subtle hover:text-white transition-colors duration-250 inline-flex items-center group"
                >
                  <span className="group-hover:translate-x-1 transition-transform duration-250">
                    News
                  </span>
                </Link>
              </li>
              <li>
                <Link
                  to="/discover"
                  className="text-white-subtle hover:text-white transition-colors duration-250 inline-flex items-center group"
                >
                  <span className="group-hover:translate-x-1 transition-transform duration-250">
                    Discover
                  </span>
                </Link>
              </li>
              <li>
                <Link
                  to="/collaborate"
                  className="text-white-subtle hover:text-white transition-colors duration-250 inline-flex items-center group"
                >
                  <span className="group-hover:translate-x-1 transition-transform duration-250">
                    Jam Studio
                  </span>
                </Link>
              </li>
              <li>
                <Link
                  to="/community"
                  className="text-white-subtle hover:text-white transition-colors duration-250 inline-flex items-center group"
                >
                  <span className="group-hover:translate-x-1 transition-transform duration-250">
                    Community
                  </span>
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <h3 className="text-sm font-semibold text-secondary uppercase tracking-wider">
              Resources
            </h3>
            <ul className="mt-5 space-y-3">
              <li>
                <Link
                  to="/help"
                  className="text-white-subtle hover:text-white transition-colors duration-250 inline-flex items-center group"
                >
                  <span className="group-hover:translate-x-1 transition-transform duration-250">
                    Help Center
                  </span>
                </Link>
              </li>
              <li>
                <Link
                  to="/about"
                  className="text-white-subtle hover:text-white transition-colors duration-250 inline-flex items-center group"
                >
                  <span className="group-hover:translate-x-1 transition-transform duration-250">
                    About Us
                  </span>
                </Link>
              </li>
              <li>
                <Link
                  to="/terms"
                  className="text-white-subtle hover:text-white transition-colors duration-250 inline-flex items-center group"
                >
                  <span className="group-hover:translate-x-1 transition-transform duration-250">
                    Terms of Service
                  </span>
                </Link>
              </li>
              <li>
                <Link
                  to="/privacy"
                  className="text-white-subtle hover:text-white transition-colors duration-250 inline-flex items-center group"
                >
                  <span className="group-hover:translate-x-1 transition-transform duration-250">
                    Privacy Policy
                  </span>
                </Link>
              </li>
              <li>
                <Link
                  to="/dmca"
                  className="text-white-subtle hover:text-white transition-colors duration-250 inline-flex items-center group"
                >
                  <span className="group-hover:translate-x-1 transition-transform duration-250">
                    DMCA Policy
                  </span>
                </Link>
              </li>
              <li>
                <Link
                  to="/cookie-policy"
                  className="text-white-subtle hover:text-white transition-colors duration-250 inline-flex items-center group"
                >
                  <span className="group-hover:translate-x-1 transition-transform duration-250">
                    Cookie Policy
                  </span>
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="text-white-subtle hover:text-white transition-colors duration-250 inline-flex items-center group"
                >
                  <span className="group-hover:translate-x-1 transition-transform duration-250">
                    Contact Us
                  </span>
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <h3 className="text-sm font-semibold text-primary uppercase tracking-wider">
              Subscribe
            </h3>
            <p className="mt-5 text-white-subtle text-base">
              Get the latest news and updates.
            </p>
            <form
              className="mt-5"
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const emailInput = form.querySelector(
                  'input[type="email"]',
                ) as HTMLInputElement;
                const email = emailInput.value;
                const button = form.querySelector(
                  "button",
                ) as HTMLButtonElement;

                if (!email) return;

                try {
                  button.disabled = true;
                  button.classList.add("opacity-50");

                  const response = await fetch(
                    `${import.meta.env.VITE_API_URL}/api/v1/newsletter/subscribe`,
                    {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ email }),
                    },
                  );

                  if (response.ok) {
                    emailInput.value = "";
                    toast.success(
                      "Subscribed!",
                      "You've been added to our newsletter.",
                    );
                  } else {
                    const error = await response.json();
                    toast.error(
                      "Error",
                      error.message || "Subscription failed",
                    );
                  }
                } catch (err) {
                  console.error("Newsletter error:", err);
                  toast.error("Error", "An error occurred. Please try again.");
                } finally {
                  button.disabled = false;
                  button.classList.remove("opacity-50");
                }
              }}
            >
              <div className="relative">
                <input
                  type="email"
                  required
                  placeholder="Enter your email"
                  className="w-full px-4 py-3 pl-10 rounded-lg bg-background-dark/60 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-250"
                />
                <Mail
                  className="absolute left-3 top-3.5 text-white-subtle"
                  size={18}
                />
                <button
                  type="submit"
                  className="absolute right-2 top-2 bg-primary hover:bg-primary-600 active:bg-primary-700 text-white p-1.5 rounded-md transition-all duration-250 flex items-center justify-center disabled:cursor-not-allowed"
                  aria-label="Subscribe"
                >
                  <ArrowRight size={18} />
                </button>
              </div>
            </form>
            <div className="mt-6 p-4 bg-primary/10 border border-primary/20 rounded-lg">
              <h4 className="font-medium text-white">Join Our Community</h4>
              <p className="mt-2 text-white-subtle text-sm">
                Connect with other music lovers and stay updated with the latest
                trends.
              </p>
              <Link
                to="/register"
                className="mt-3 inline-flex items-center text-primary hover:text-primary-light text-sm font-medium group"
              >
                Sign up for free
                <ArrowRight
                  size={16}
                  className="ml-2 group-hover:translate-x-1 transition-transform duration-250"
                />
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-16 border-t border-white/5 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-white-subtle text-sm">
            © 2025 Bass Ministry. All rights reserved.
          </p>
          <p className="text-white-subtle text-sm mt-4 md:mt-0">
            Designed with{" "}
            <span className="text-primary text-glow-primary-subtle inline-block animate-pulse">
              ♥
            </span>{" "}
            for music lovers everywhere
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
